import {
  DependencyError,
  equalsIgnoreCase,
  medicationAdherences
} from '@cumu/shared';
import { target, targets } from '@github/catalyst';
import type { AutoCompleteProvideEventDetail } from './auto-complete-behavior';
import { controller } from './controller';
import { dispatchChange } from './util';

const fragment = document.createElement('ul');
fragment.setAttribute('aria-label', 'Suggestions');
fragment.className = 'suggester suggester-container mt-1';
fragment.setAttribute('role', 'listbox');

@controller('medication-list-question')
class MedicationListQuestionElement extends HTMLElement {
  @target declare valueInput: HTMLInputElement;
  @target declare listElement: HTMLUListElement;
  @target declare addButton: HTMLButtonElement;
  @target declare prescriberList: HTMLDataListElement;
  @targets declare prescriberInputs: HTMLInputElement[];
  @targets declare drugbankProductConceptInputs: HTMLInputElement[];
  @target declare drugDrugInteractionsContent: HTMLDivElement;
  drugDrugInteractionsTimeout = 0;

  checkDrugDrugInteractions() {
    const ids = this.drugbankProductConceptInputs
      .map(x => x.value)
      .filter(x => x)
      .join(',');
    if (ids === this.getAttribute('drugbank-pcids')) {
      return;
    }
    clearTimeout(this.drugDrugInteractionsTimeout);
    setTimeout(async () => {
      const params = new URLSearchParams([
        ['drugbank_pcids', ids],
        ['target', 'medication-list-question.drugDrugInteractionsContent']
      ]);
      const url = new URL(
        `/api/medications/interactions?${params}`,
        location.origin
      );
      const response = await fetch(url);
      if (!response.ok) {
        throw new DependencyError(response, await response.text());
      }
      const el = this.drugDrugInteractionsContent;
      el.insertAdjacentHTML('afterend', await response.text());
      el.remove();
      this.setAttribute('drugbank-pcids', ids);
    }, 300);
  }

  valueInputFocused() {
    this.addButton.focus();
  }

  updateValueElement() {
    const newValue = String(this.listElement.children.length || ''); // encode zero as empty string
    if (this.valueInput.value !== newValue) {
      this.valueInput.value = newValue;
      dispatchChange(this.valueInput);
    }
  }

  providePrescribers({
    detail: { provide, relatedTarget }
  }: CustomEvent<AutoCompleteProvideEventDetail>) {
    const q = relatedTarget.value.trim().toLowerCase();
    provide(
      Promise.resolve({
        suggestions: this.prescriberInputs
          .filter(x => x !== relatedTarget)
          .map(x => x.value.trim())
          .filter((x, i, a) => a.findIndex(y => equalsIgnoreCase(y, x)) === i)
          .filter(x => x.toLowerCase().includes(q))
          .sort((a, b) => {
            const ai = clampIndex(a.toLowerCase().indexOf(q));
            const bi = clampIndex(b.toLowerCase().indexOf(q));
            return ai - bi || a.localeCompare(b, 'en', { sensitivity: 'base' });
          })
          .map(id => ({ id, title: id }))
      })
    );
  }

  provideAdherence({
    detail: { provide, relatedTarget }
  }: CustomEvent<AutoCompleteProvideEventDetail>) {
    const q = relatedTarget.value.trim().toLowerCase();
    provide(
      Promise.resolve({
        suggestions:
          q.length === 0
            ? medicationAdherences
            : medicationAdherences
                .filter(x => x.title.toLowerCase().includes(q))
                .sort((a, b) => {
                  const ai = clampIndex(a.title.toLowerCase().indexOf(q));
                  const bi = clampIndex(b.title.toLowerCase().indexOf(q));
                  return (
                    ai - bi ||
                    a.title.localeCompare(b.title, 'en', {
                      sensitivity: 'base'
                    })
                  );
                })
      })
    );
  }

  provideFrequency({
    detail: { provide, relatedTarget }
  }: CustomEvent<AutoCompleteProvideEventDetail>) {
    const pattern =
      /^ *(?:(take|give|apply|instill|inject|inhale) +)?(\d+(?:\.\d+|\/\d+)?)(?: +)?(\w+)?( +)?(\w+)?( +)?(\w+)?( +)?/i;
    const q = relatedTarget.value; // do not trim
    const [
      ,
      verb,
      quantity,
      unit,
      unitDelim,
      route,
      routeDelim,
      frequency,
      frequencyDelim
    ] = q.match(pattern) ?? [];
    this.#log(
      JSON.stringify(
        {
          verb,
          quantity,
          unit,
          unitDelim,
          route,
          routeDelim,
          frequency,
          frequencyDelim
        },
        null,
        2
      )
    );
    let options: { abbr: string; def: string }[];
    let term: string;
    let template: (item: { abbr: string; def: string }) => string;
    if (frequencyDelim || frequencies.some(f => f.abbr === frequency)) {
      // completed - no suggestions
      options = [];
      term = '';
      template = () => '';
    } else if (
      routeDelim ||
      routes.some(r => r.abbr === route?.toLowerCase())
    ) {
      // suggest frequencies
      options = frequencies;
      term = frequency;
      template = ({ abbr }) =>
        [verb, quantity, unit, route, abbr].join(' ').trim();
    } else if (unitDelim || units.some(u => u.abbr === unit)) {
      // suggest routes
      const routeSubset = units.find(
        x => x.abbr === unit.toLowerCase()
      )?.routes;
      options = routeSubset
        ? routes.filter(x => routeSubset.includes(x.abbr))
        : routes;
      term = route;
      template = ({ abbr }) =>
        [verb, quantity, unit, abbr].join(' ').trim() + ' ';
    } else if (quantity) {
      // suggest units
      const drugbankRoute =
        relatedTarget
          .closest('li')
          ?.querySelector<HTMLInputElement>('input[name*="/route:"]')?.value ??
        '';
      const unitSubset = routeToUnits[drugbankRoute] ?? [];
      options = units
        .filter(x => unitSubset.length === 0 || unitSubset.includes(x.abbr))
        .map(x => (quantity === '1' ? x : { abbr: x.abbr, def: x.def + 's' }));
      term = unit;
      template = ({ abbr }) => [verb, quantity, abbr].join(' ').trim() + ' ';
    } else {
      // no suggestions- do not need to suggest verbs, they're optional.
      options = [];
      term = '';
      template = () => '';
    }
    if (term) {
      const lowerTerm = term.toLowerCase();
      options = options
        .filter(
          x =>
            x.abbr.toLowerCase().includes(term) ||
            x.def.toLowerCase().includes(lowerTerm)
        )
        .sort((a, b) => {
          const ai = Math.min(
            clampIndex(a.abbr.toLowerCase().indexOf(lowerTerm)),
            clampIndex(a.def.toLowerCase().indexOf(lowerTerm))
          );
          const bi = Math.min(
            clampIndex(b.abbr.toLowerCase().indexOf(lowerTerm)),
            clampIndex(b.def.toLowerCase().indexOf(lowerTerm))
          );
          return (
            ai - bi ||
            a.abbr.localeCompare(b.abbr, 'en', { sensitivity: 'base' })
          );
        });
    }
    provide(
      Promise.resolve({
        suggestions: options.map(x => ({
          id: template(x),
          title: `${x.abbr}: ${x.def}`
        }))
      })
    );
  }

  #log(..._args: any[]) {
    if (location.hostname === 'local.cumulus.care') {
      console.log('[medication-list-question]', ..._args);
    }
  }
}

function clampIndex(n: number) {
  return n === -1 ? Number.MAX_SAFE_INTEGER : n;
}

type UnitAbbr =
  | 'tab'
  | 'cap'
  | 'gtt'
  | 'mcg'
  | 'mg'
  | 'g'
  | 'mEq'
  | 'mL'
  | 'unit'
  | 'puff'
  | 'patch'
  | 'IU'
  | 'supp'
  | 'amp'
  | 'vial';
type RouteAbbr =
  | 'po'
  | 'sl'
  | 'im'
  | 'iv'
  | 'sc'
  | 'pr'
  | 'id'
  | 'inh'
  | 'top'
  | 'od'
  | 'os'
  | 'ou'
  | 'ad'
  | 'as'
  | 'au'
  | 'transdermal'
  | 'vag'
  | 'nasal';

// drugbank routes to units
const routeToUnits: Record<string, UnitAbbr[]> = {
  'Auricular (otic)': ['gtt', 'mL'], // Ear drops, solutions
  Buccal: ['tab', 'mg', 'mL'], // Buccal tablets, lozenges
  Conjunctival: ['gtt', 'mL'], // Eye drops, solutions
  Cutaneous: ['g', 'mL', 'patch'], // Creams, ointments, patches
  Dental: ['g', 'mg', 'mL'], // Pastes, gels, solutions
  'Electro-osmosis': ['mg', 'mL'], // Drug-infused solutions
  Endocervical: ['g', 'mg', 'mL'], // Gels, creams
  Endosinusial: ['gtt', 'mL'], // Nasal solutions, sprays
  Endotracheal: ['mg', 'mL'], // Nebulized drugs
  Enteral: ['tab', 'cap', 'mg', 'mL'], // Oral tablets, liquids
  Epidural: ['mg', 'mL', 'IU'], // Anesthetics, pain meds
  Extracorporeal: ['mg', 'mL'], // Dialysis-related drugs
  Hemodialysis: ['mg', 'mL', 'IU'], // IV medications
  Infiltration: ['mg', 'mL'], // Local anesthetics
  Interstitial: ['mg', 'mL'], // Fluids, chemotherapeutics
  'Intra-amniotic': ['mg', 'mL'], // Steroids, antibiotics
  'Intra-arterial': ['mg', 'mL', 'IU', 'vial'], // Infusions, chemotherapy
  'Intra-articular': ['mg', 'mL'], // Steroids, joint injections
  Intrabiliary: ['mg', 'mL'], // Biliary contrast agents
  Intrabronchial: ['mg', 'mL', 'puff'], // Nebulized solutions
  Intrabursal: ['mg', 'mL'], // Joint steroid injections
  Intracardiac: ['mg', 'mL', 'IU'], // Emergency cardiac drugs
  Intracaudal: ['mg', 'mL'], // Pain management
  Intracavernous: ['mg', 'mL'], // Erectile dysfunction injections
  Intracavitary: ['mg', 'mL'], // Chemotherapy, anesthesia
  Intracerebral: ['mg', 'mL'], // Research, experimental
  Intracisternal: ['mg', 'mL'], // CSF-related drugs
  Intracoronary: ['mg', 'mL'], // Cardiac contrast agents
  Intradermal: ['mg', 'mL', 'unit'], // Vaccines, local anesthetics
  Intradiscal: ['mg', 'mL'], // Steroid injections
  Intraduodenal: ['mg', 'mL'], // Enteral nutrition
  Intragastric: ['mg', 'mL'], // Oral liquids, feeding tubes
  Intraileal: ['mg', 'mL'], // GI-related drugs
  Intralesional: ['mg', 'mL'], // Steroids, chemotherapy
  Intramuscular: ['mg', 'mL', 'unit', 'IU', 'vial', 'amp'], // Vaccines, antibiotics
  Intraocular: ['gtt', 'mg', 'mL'], // Eye drops, intraocular injections
  Intraperitoneal: ['mg', 'mL', 'IU'], // Dialysis, chemotherapy
  Intrapleural: ['mg', 'mL'], // Chemotherapy, anesthetics
  Intrapulmonary: ['mg', 'mL', 'puff'], // Inhaled medications
  Intraspinal: ['mg', 'mL', 'IU'], // Pain management, anesthetics
  Intrasynovial: ['mg', 'mL'], // Joint injections
  Intrathecal: ['mg', 'mL'], // Pain management, chemotherapy
  Intratumor: ['mg', 'mL'], // Tumor-targeted therapies
  Intrauterine: ['mg', 'mL', 'IU'], // IUD drug release
  Intravenous: ['mg', 'mL', 'unit', 'IU', 'vial', 'amp'], // IV fluids, antibiotics
  'Intravenous bolus': ['mg', 'mL', 'unit', 'IU', 'vial', 'amp'], // Emergency medications
  'Intravenous drip': ['mg', 'mL', 'unit', 'IU'], // Continuous IV therapy
  Nasal: ['gtt', 'mg', 'mL', 'puff'], // Nasal sprays, solutions
  Nasogastric: ['mg', 'mL'], // Tube feeding, medications
  Ophthalmic: ['gtt', 'mg', 'mL'], // Eye drops, ointments
  Oral: ['tab', 'cap', 'mg', 'mL'], // Tablets, capsules, solutions
  Oropharyngeal: ['mg', 'mL'], // Sprays, lozenges
  Parenteral: ['mg', 'mL', 'unit', 'IU', 'vial', 'amp'], // Injectable medications
  Percutaneous: ['g', 'mL', 'patch'], // Topical medications
  Periodontal: ['mg', 'mL'], // Dental gels, rinses
  Rectal: ['mg', 'mL', 'supp'], // Suppositories, enemas
  'Respiratory (inhalation)': ['mg', 'mL', 'puff'], // Inhalers, nebulizers
  'Soft tissue': ['mg', 'mL'], // Local injections
  Subcutaneous: ['mg', 'mL', 'unit', 'IU', 'vial', 'amp'], // Insulin, heparin
  Sublingual: ['tab', 'mg'], // SL tablets, sprays
  Submucosal: ['mg', 'mL'], // Local anesthetics
  Topical: ['g', 'mL', 'patch'], // Creams, ointments
  Transdermal: ['mg', 'mL', 'patch'], // Patches, creams
  Transmucosal: ['mg', 'mL'], // Buccal, SL routes
  Urethral: ['mg', 'mL', 'supp'], // Instillations
  Vaginal: ['mg', 'mL', 'g', 'supp'] // Creams, tablets, suppositories
};

// Unit abbreviations
const units: { abbr: UnitAbbr; def: string; routes?: RouteAbbr[] }[] = [
  { abbr: 'tab', def: 'Tablet', routes: ['po', 'sl', 'pr'] }, // Take 1 tab po bid
  { abbr: 'cap', def: 'Capsule', routes: ['po', 'sl', 'pr'] }, // Take 2 caps po qhs
  {
    abbr: 'gtt',
    def: 'Drop',
    routes: ['po', 'sl', 'pr', 'iv', 'top', 'od', 'os', 'ou', 'ad', 'as', 'au']
  }, // Instill 1 gtt OS q8h
  {
    abbr: 'mcg',
    def: 'Microgram',
    routes: ['po', 'iv', 'im', 'sc', 'inh']
  }, // Take 100 mcg po qd
  {
    abbr: 'mg',
    def: 'Milligram',
    routes: ['po', 'iv', 'im', 'sc', 'inh', 'top', 'pr']
  }, // Take 500 mg po tid
  {
    abbr: 'g',
    def: 'Gram',
    routes: ['po', 'top', 'pr']
  }, // Apply 2 g top q6h
  {
    abbr: 'mEq',
    def: 'Milliequivalent',
    routes: ['po', 'iv']
  }, // Take 20 mEq po qd
  {
    abbr: 'mL',
    def: 'Milliliter',
    routes: ['po', 'iv', 'im', 'sc', 'id', 'pr', 'top', 'inh']
  }, // Give 5 mL po bid
  {
    abbr: 'unit',
    def: 'Unit',
    routes: ['sc', 'iv', 'im']
  }, // Inject 10 units SC bid
  {
    abbr: 'puff',
    def: 'Puff',
    routes: ['inh', 'nasal']
  }, // Take 2 puffs INH q4h
  {
    abbr: 'patch',
    def: 'Transdermal Patch',
    routes: ['transdermal', 'top']
  }, // Apply 1 patch TD q24h
  {
    abbr: 'IU',
    def: 'International Unit',
    routes: ['iv', 'im', 'sc']
  }, // Inject 5000 IU SC qd
  {
    abbr: 'supp',
    def: 'Suppository',
    routes: ['pr', 'vag']
  }, // Insert 1 supp PR qhs
  {
    abbr: 'amp',
    def: 'Ampule',
    routes: ['iv', 'im', 'sc']
  }, // Administer 1 amp IV stat
  {
    abbr: 'vial',
    def: 'Vial',
    routes: ['iv', 'im', 'sc']
  } // Administer 1 vial IM q4w
];

// Route of Administration Abbreviations
const routes: { abbr: RouteAbbr; def: string }[] = [
  { abbr: 'po', def: 'By mouth' }, // Take 1 tab po qd
  { abbr: 'sl', def: 'Sublingual' }, // Take 1 tab SL prn
  { abbr: 'im', def: 'Intramuscular' }, // Inject 5 mg IM stat
  { abbr: 'iv', def: 'Intravenous' }, // Give 1 L IV q12h
  { abbr: 'sc', def: 'Subcutaneous' }, // Inject 10 units SC bid (under skin)  (preferred over SQ)
  { abbr: 'pr', def: 'Per rectum' }, // Insert 1 supp PR qhs
  { abbr: 'id', def: 'Intradermal' }, // Inject 0.1 mL ID PPD test (shallow injection)
  { abbr: 'inh', def: 'Inhaled' }, // Take 2 puffs INH q4h prn
  { abbr: 'top', def: 'Topical' }, // Apply 1 g TOP q8h
  { abbr: 'od', def: 'Right eye' }, // Instill 1 gtt OD q6h
  { abbr: 'os', def: 'Left eye' }, // Instill 1 gtt OS q6h
  { abbr: 'ou', def: 'Both eyes' }, // Instill 1 gtt OU q12h
  { abbr: 'ad', def: 'Right ear' }, // Instill 2 gtt AD bid
  { abbr: 'as', def: 'Left ear' }, // Instill 2 gtt AS bid
  { abbr: 'au', def: 'Both ears' } // Instill 2 gtt AU bid
];

// Time & Frequency Abbreviations
const frequencies = [
  { abbr: 'qd', def: 'Once daily' }, // Take 1 tab po qd
  { abbr: 'bid', def: 'Twice daily' }, // Take 1 tab po bid
  { abbr: 'tid', def: 'Three times daily' }, // Take 1 tab po tid
  { abbr: 'qid', def: 'Four times daily' }, // Take 1 tab po qid
  // { abbr: 'qod', def: 'Every other day (discouraged)' }, // Take 1 tab po qod
  { abbr: 'qam', def: 'Every morning' }, // Take 1 tab po qam
  { abbr: 'qpm', def: 'Every evening' }, // Take 1 tab po qpm
  { abbr: 'qhs', def: 'Every night at bedtime' }, // Take 1 tab po qhs
  { abbr: 'q1h', def: 'Every hour' }, // Take 1 tab po q6h
  { abbr: 'q2h', def: 'Every 2 hours' }, // Take 1 tab po q6h
  { abbr: 'q3h', def: 'Every 3 hours' }, // Take 1 tab po q6h
  { abbr: 'q4h', def: 'Every 4 hours' }, // Take 1 tab po q6h
  { abbr: 'q6h', def: 'Every 6 hours' }, // Take 1 tab po q6h
  { abbr: 'q8h', def: 'Every 8 hours' }, // Take 1 tab po q6h
  { abbr: 'qwk', def: 'Every week' }, // Take 1 tab po qwk
  { abbr: 'prn', def: 'As needed' }, // Take 1 tab po q6h prn pain
  { abbr: 'stat', def: 'Immediately' }, // Inject 5 mg IM stat
  { abbr: 'ac', def: 'Before meals' }, // Take 1 tab po AC
  { abbr: 'pc', def: 'After meals' } // Take 1 tab po PC
];

declare global {
  interface Window {
    MedicationListQuestionElement: typeof MedicationListQuestionElement;
  }

  interface HTMLElementTagNameMap {
    'medication-list-question': MedicationListQuestionElement;
  }
}
